import { useCallback, useRef } from 'react';
import { useSettingsStore } from '../stores';

export type SoundEffect =
  | 'cardPlay'
  | 'cardDeal'
  | 'trickWon'
  | 'callMade'
  | 'yourTurn'
  | 'gameOver'
  | 'buttonClick'
  | 'error'
  | 'timerLow';

const SOUND_FREQUENCIES: Record<SoundEffect, { freq: number; duration: number; type: OscillatorType }> = {
  cardPlay:    { freq: 800,  duration: 80,  type: 'sine' },
  cardDeal:    { freq: 600,  duration: 60,  type: 'sine' },
  trickWon:    { freq: 1000, duration: 200, type: 'triangle' },
  callMade:    { freq: 700,  duration: 120, type: 'sine' },
  yourTurn:    { freq: 880,  duration: 150, type: 'square' },
  gameOver:    { freq: 440,  duration: 400, type: 'triangle' },
  buttonClick: { freq: 1200, duration: 30,  type: 'sine' },
  error:       { freq: 200,  duration: 250, type: 'sawtooth' },
  timerLow:    { freq: 660,  duration: 100, type: 'square' },
};

export function useSoundEffects() {
  const soundEnabled = useSettingsStore((s) => s.soundEnabled);
  const soundVolume = useSettingsStore((s) => s.soundVolume);
  const ctxRef = useRef<AudioContext | null>(null);

  const getContext = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
    }
    return ctxRef.current;
  }, []);

  const play = useCallback(
    (effect: SoundEffect) => {
      if (!soundEnabled) return;

      const config = SOUND_FREQUENCIES[effect];
      const ctx = getContext();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = config.type;
      osc.frequency.setValueAtTime(config.freq, ctx.currentTime);

      gain.gain.setValueAtTime(soundVolume * 0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + config.duration / 1000);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + config.duration / 1000);
    },
    [soundEnabled, soundVolume, getContext],
  );

  return { play };
}
